import React from 'react';
import {
  Box,
  Typography,
  Chip,
  CardMedia,
} from '@mui/material';
import whatsappBg from '../../../../assets/images/backgrounds/fondodoodle2.png';

const TEMPLATE_STATUS_STYLE = {
  APPROVED: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
  PENDING: { backgroundColor: '#FFF3E0', color: '#EF6C00' },
  FAILED: { backgroundColor: '#FDECEA', color: '#C62828' },
  REJECTED: { backgroundColor: '#FDECEA', color: '#C62828' },
};

const QUALITY_STYLE = {
  GREEN: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
  YELLOW: { backgroundColor: '#FFF8E1', color: '#F57F17' },
  RED: { backgroundColor: '#FDECEA', color: '#C62828' },
  UNKNOWN: { backgroundColor: '#F5F5F5', color: '#9E9E9E' },
};

const CATEGORY_TEXT_COLOR = {
  UTILITY: { backgroundColor: '#f2f2ff', color: '#0016ff' },
  MARKETING: { backgroundColor: '#f6f3ff', color: '#4700ff' },
  AUTH: { backgroundColor: '#f9f0ff', color: '#8c00ff' },
};

const TEMPLATE_TYPE_STYLE = {
  TEXT: { backgroundColor: '#F5F7FA', color: '#37474F' },
  IMAGE: { backgroundColor: '#E3F2FD', color: '#1565C0' },
  VIDEO: { backgroundColor: '#E1F5FE', color: '#0277BD' },
  DOCUMENT: { backgroundColor: '#ECEFF1', color: '#455A64' },
  LOCATION: { backgroundColor: '#E0F2F1', color: '#00695C' },
  CAROUSEL: { backgroundColor: '#F3E5F5', color: '#6A1B9A' },
};

const formatLang = (code) => {
  if (!code) return null;
  return code.split('_')[0]?.toUpperCase();
};

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
};

const analyzeComponents = (components) => {
  if (!components?.length) return { headerType: null, hasFooter: false, paramCount: 0 };
  const header = components.find((c) => c.type === 'header');
  const hasFooter = components.some((c) => c.type === 'footer');
  let paramCount = 0;
  components.forEach((c) => {
    if (c?.parameters?.length) paramCount += c.parameters.length;
  });
  return { headerType: header?.format || null, hasFooter, paramCount };
};

export const TemplateDetailPreview = ({ template }) => {
  if (!template) return null;

  const langLabel = formatLang(template.language_code);
  const dateLabel = fmtDate(template.date_create);
  const isRejected = template.status === 'REJECTED';
  const quality = template.quality && template.quality !== 'UNKNOWN' ? template.quality : null;
  const compInfo = analyzeComponents(template.components);

  // Body text for preview
  let bodyText = template.example || template.body || '';

  // Image for preview
  let imageUrl = template.url;
  const carouselComponent = template.components?.find((c) => c.type === 'carousel');
  const carouselCards = carouselComponent?.cards;

  if (!carouselCards && bodyText.includes('|')) {
    const parts = bodyText.split('|');
    if (parts.length >= 3 && (parts[0].startsWith('http') || parts[0].startsWith('https'))) {
      imageUrl = parts[0];
      bodyText = parts.slice(1).join(' | ');
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      {/* WhatsApp preview thumbnail */}
      <Box
        sx={{
          width: 160,
          minWidth: 160,
          height: 130,
          backgroundImage: `url(${whatsappBg})`,
          backgroundSize: 'cover',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 1.5,
            p: imageUrl ? 0 : 1,
            width: '90%',
            maxWidth: 140,
            height: 110,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {imageUrl && (
            <CardMedia
              component="img"
              image={imageUrl}
              alt="template-header"
              sx={{
                mb: 0.5,
                width: '100%',
                height: 60,
                minHeight: 60,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ px: 0.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {bodyText?.slice(0, 100) || 'Sin contenido'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Template metadata */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {/* Name */}
        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
          {template.name}
        </Typography>

        {/* Chips row */}
        <Box display="flex" gap={0.5} flexWrap="wrap">
          <Chip
            label={template.status || 'N/A'}
            size="small"
            sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem', ...TEMPLATE_STATUS_STYLE[template.status] }}
          />
          {quality && (
            <Chip
              label={quality}
              size="small"
              sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem', ...QUALITY_STYLE[quality] }}
            />
          )}
          {template.category && (
            <Chip
              label={template.category}
              size="small"
              sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem', ...CATEGORY_TEXT_COLOR[template.category] }}
            />
          )}
          {template.type && (
            <Chip
              label={template.type.toUpperCase()}
              size="small"
              sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem', ...TEMPLATE_TYPE_STYLE[template?.type?.toUpperCase()] }}
            />
          )}
          {langLabel && (
            <Chip
              label={langLabel}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500, height: 22, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {/* Structure info */}
        {(compInfo.headerType || compInfo.hasFooter || compInfo.paramCount > 0) && (
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {compInfo.headerType && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Header: {compInfo.headerType.toUpperCase()}
              </Typography>
            )}
            {compInfo.paramCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {compInfo.paramCount} param{compInfo.paramCount !== 1 ? 's' : ''}
              </Typography>
            )}
            {compInfo.hasFooter && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                + footer
              </Typography>
            )}
          </Box>
        )}

        {/* Date and reason */}
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {dateLabel && (
            <Typography variant="caption" color="text.secondary">
              Creado: {dateLabel}
            </Typography>
          )}
        </Box>

        {isRejected && template.reason && (
          <Typography variant="caption" color="error" sx={{ fontStyle: 'italic' }}>
            Motivo: {template.reason}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TemplateDetailPreview;
